import type { SlaTargetMetricEnumKey } from '../../../core/api/generated/models/SlaTargetMetric';
import type { SlaTargetTrackEnumKey } from '../../../core/api/generated/models/SlaTargetTrack';

/**
 * Uma combinação (métrica, track) editável na tela de Preferências.
 *
 * A unidade muda por track de propósito: uma IA responde em segundos, e obrigar o gestor a digitar
 * "0,5 minuto" seria absurdo — foi por isso que o piso antigo de 60s não fazia sentido depois que o
 * modelo passou a separar humano de IA.
 */
/**
 * Chave canônica de uma combinação (métrica, track).
 *
 * Existe porque a concatenação estava escrita à mão em quatro lugares (os slots, a store do inbox, a
 * tela de Preferências e o selo). Se qualquer um divergisse do separador, `targets[chave]` viraria
 * `undefined`, `computeSlaBadge` devolveria `null` e o selo simplesmente DESAPARECERIA de todas as
 * conversas — sem erro, sem log, sem nada que apontasse a causa.
 */
export function slotKey(metric: SlaTargetMetricEnumKey, track: SlaTargetTrackEnumKey): string {
  return `${metric}|${track}`;
}

export interface SlaTargetSlot {
  /** `metric|track` — mesma chave usada para indexar as metas vindas da API. Use `slotKey`. */
  readonly key: string;
  readonly metric: SlaTargetMetricEnumKey;
  readonly track: SlaTargetTrackEnumKey;
  readonly label: string;
  readonly hint: string;
  readonly unit: 'seconds' | 'minutes';
  readonly unitLabel: string;
  /** Renderiza o valor guardado (sempre em segundos) para leitura. */
  readonly format: (seconds: number) => string;
}

function minutesLabel(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

function secondsLabel(seconds: number): string {
  return seconds < 60 ? `${seconds}s` : minutesLabel(seconds);
}

export const SLA_TARGET_SLOTS: readonly SlaTargetSlot[] = [
  {
    key: slotKey('first_reply', 'human'),
    metric: 'first_reply',
    track: 'human',
    label: '1ª resposta — humano',
    hint: 'Tempo para alguém do time responder a primeira mensagem do cliente, contado em horário comercial.',
    unit: 'minutes',
    unitLabel: 'min',
    format: minutesLabel,
  },
  {
    key: slotKey('first_reply', 'ai'),
    metric: 'first_reply',
    track: 'ai',
    label: '1ª resposta — IA',
    hint: 'Tempo para o agente de IA responder a primeira mensagem, quando a conversa está com ele.',
    unit: 'seconds',
    unitLabel: 'seg',
    format: secondsLabel,
  },
  {
    key: slotKey('next_reply', 'human'),
    metric: 'next_reply',
    track: 'human',
    label: 'Próximas respostas — humano',
    hint: 'Tempo para responder as demais mensagens do cliente ao longo da conversa.',
    unit: 'minutes',
    unitLabel: 'min',
    format: minutesLabel,
  },
  {
    key: slotKey('next_reply', 'ai'),
    metric: 'next_reply',
    track: 'ai',
    label: 'Próximas respostas — IA',
    hint: 'Tempo para o agente de IA responder as demais mensagens da conversa.',
    unit: 'seconds',
    unitLabel: 'seg',
    format: secondsLabel,
  },
];

/** Segundos guardados → valor exibido no input, na unidade do slot. Null vira campo vazio. */
export function secondsToInputValue(seconds: number | null, slot: SlaTargetSlot): string {
  if (seconds === null) return '';
  return slot.unit === 'minutes' ? String(Math.round(seconds / 60)) : String(seconds);
}

/** Valor digitado → segundos. Null quando não é um número positivo válido. */
export function slotInputValueToSeconds(raw: string, slot: SlaTargetSlot): number | null {
  const parsed = Number(raw.replace(',', '.'));
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const seconds = slot.unit === 'minutes' ? Math.round(parsed * 60) : Math.round(parsed);
  return seconds > 0 ? seconds : null;
}
